// MCHD Room Scheduler - ChangeLog React Query Hook
// React Query hooks for change log operations
// Created: 08-10-2025 18:36:45
// Updated: 09-10-2025 00:42:19 - Fixed auth token retrieval bug

import { useQuery } from '@tanstack/react-query';
import { API_CONFIG } from '../../api';
import { TokenManager } from '../../api/interceptors';
import type { 
  ChangeLog, 
  ChangeLogFilters, 
  ChangeLogsResponse 
} from '@mchd/shared';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const changeLogQueryKeys = {
  all: ['change-logs'] as const,
  lists: () => [...changeLogQueryKeys.all, 'list'] as const,
  list: (filters: ChangeLogFilters) => [...changeLogQueryKeys.lists(), filters] as const,
  byEntity: (entityType: string, entityId: string, filters?: ChangeLogFilters) => 
    [...changeLogQueryKeys.all, 'entity', entityType, entityId, filters] as const,
  stats: (filters?: Pick<ChangeLogFilters, 'entityType' | 'startDate' | 'endDate'>) => 
    [...changeLogQueryKeys.all, 'stats', filters] as const,
  userRecent: (userId: string, limit?: number) => 
    [...changeLogQueryKeys.all, 'user', userId, limit] as const,
  check: (entityType: string, entityId: string) => 
    [...changeLogQueryKeys.all, 'check', entityType, entityId] as const,
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetch change logs by entity
 * @param entityType - Entity type (assignment, room, etc.)
 * @param entityId - Entity ID
 * @param filters - Optional filters
 * @returns Change logs response
 */
async function fetchChangeLogsByEntity(
  entityType: string,
  entityId: string,
  filters?: ChangeLogFilters
): Promise<ChangeLogsResponse> {
  const queryParams = new URLSearchParams();
  
  if (filters?.limit) queryParams.append('limit', filters.limit.toString());
  if (filters?.offset) queryParams.append('offset', filters.offset.toString());
  if (filters?.action) queryParams.append('action', filters.action);
  if (filters?.userId) queryParams.append('userId', filters.userId);
  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);

  const url = `${API_CONFIG.BASE_URL}/change-logs/${entityType}/${entityId}${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;

  const token = TokenManager.getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch change logs');
  }

  return result;
}

/**
 * Fetch all change logs with filtering
 * @param filters - Filter parameters
 * @returns Change logs response
 */
async function fetchAllChangeLogs(filters?: ChangeLogFilters): Promise<ChangeLogsResponse> {
  const queryParams = new URLSearchParams();
  
  if (filters?.entityType) queryParams.append('entityType', filters.entityType);
  if (filters?.entityId) queryParams.append('entityId', filters.entityId);
  if (filters?.userId) queryParams.append('userId', filters.userId);
  if (filters?.action) queryParams.append('action', filters.action);
  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);
  if (filters?.limit) queryParams.append('limit', filters.limit.toString());
  if (filters?.offset) queryParams.append('offset', filters.offset.toString());

  const url = `${API_CONFIG.BASE_URL}/change-logs${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;

  const token = TokenManager.getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch change logs');
  }

  return result;
}

/**
 * Fetch change log statistics
 * @param filters - Filter parameters
 * @returns Statistics response
 */
async function fetchChangeLogStats(
  filters?: Pick<ChangeLogFilters, 'entityType' | 'startDate' | 'endDate'>
): Promise<any> {
  const queryParams = new URLSearchParams();
  
  if (filters?.entityType) queryParams.append('entityType', filters.entityType);
  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);

  const url = `${API_CONFIG.BASE_URL}/change-logs/stats${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;

  const token = TokenManager.getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch statistics');
  }

  return result;
}

/**
 * Fetch recent changes by user
 * @param userId - User ID
 * @param limit - Maximum number of records
 * @returns Change logs response
 */
async function fetchRecentChangesByUser(
  userId: string,
  limit: number = 20
): Promise<ChangeLogsResponse> {
  const url = `${API_CONFIG.BASE_URL}/change-logs/user/${userId}?limit=${limit}`;

  const token = TokenManager.getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch recent changes');
  }

  return result;
}

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

/**
 * Hook to get change logs for a specific entity
 * @param entityType - Entity type (assignment, room, etc.)
 * @param entityId - Entity ID
 * @param filters - Optional filters
 * @param options - Query options
 * @returns Change logs query result
 */
export function useChangeLogs(
  entityType: string,
  entityId: string,
  filters?: ChangeLogFilters,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchInterval?: number;
  }
) {
  return useQuery({
    queryKey: changeLogQueryKeys.byEntity(entityType, entityId, filters),
    queryFn: () => fetchChangeLogsByEntity(entityType, entityId, filters),
    enabled: options?.enabled !== false && !!entityType && !!entityId,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes (audit logs are immutable)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to get all change logs with filtering
 * @param filters - Filter parameters
 * @param options - Query options
 * @returns Change logs query result
 */
export function useAllChangeLogs(
  filters?: ChangeLogFilters,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: changeLogQueryKeys.list(filters || {}),
    queryFn: () => fetchAllChangeLogs(filters),
    enabled: options?.enabled !== false,
    staleTime: options?.staleTime || 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to get change log statistics
 * @param filters - Filter parameters
 * @param options - Query options
 * @returns Statistics query result
 */
export function useChangeLogStats(
  filters?: Pick<ChangeLogFilters, 'entityType' | 'startDate' | 'endDate'>,
  options?: {
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: changeLogQueryKeys.stats(filters),
    queryFn: () => fetchChangeLogStats(filters),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to get recent changes by user
 * @param userId - User ID
 * @param limit - Maximum number of records
 * @param options - Query options
 * @returns Recent changes query result
 */
export function useRecentChangesByUser(
  userId: string,
  limit: number = 20,
  options?: {
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: changeLogQueryKeys.userRecent(userId, limit),
    queryFn: () => fetchRecentChangesByUser(userId, limit),
    enabled: options?.enabled !== false && !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute for recent changes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Helper function to format change log for display
 * @param log - Change log
 * @returns Formatted change log data
 */
export function formatChangeLog(log: ChangeLog): {
  id: string;
  action: string;
  actionLabel: string;
  actionColor: string;
  entityType: string;
  entityId: string;
  changedFields: string[];
  beforeSnapshot: any;
  afterSnapshot: any;
  performedBy: string;
  performedAt: string;
  performedAtFormatted: string;
} {
  const actionLabels: Record<string, string> = {
    CREATE: 'Created',
    UPDATE: 'Updated',
    DELETE: 'Deleted',
    PUBLISH: 'Published',
    UNPUBLISH: 'Unpublished',
  };

  const actionColors: Record<string, string> = {
    CREATE: 'green',
    UPDATE: 'blue',
    DELETE: 'red',
    PUBLISH: 'teal',
    UNPUBLISH: 'gray',
  };

  return {
    id: log.id,
    action: log.action,
    actionLabel: actionLabels[log.action] || log.action,
    actionColor: actionColors[log.action] || 'gray',
    entityType: log.entityType,
    entityId: log.entityId,
    changedFields: log.changes?.fields || [],
    beforeSnapshot: log.changes?.before || null,
    afterSnapshot: log.changes?.after || null,
    performedBy: log.user?.name || 'Unknown',
    performedAt: log.createdAt.toString(),
    performedAtFormatted: new Date(log.createdAt).toLocaleString(),
  };
}

